import { useEffect, useRef, useState } from 'react';
import { X, HelpCircle, Zap, ScanBarcode, FileText, Image as ImageIcon, RotateCcw, Activity, Camera, RefreshCw, Settings } from 'lucide-react';

interface AICameraScannerProps {
  onCapture: (imageData: string) => void;
  onClose: () => void;
}

type CameraState = 'checking' | 'requesting' | 'active' | 'denied' | 'blocked' | 'error';

export default function AICameraScanner({ onCapture, onClose }: AICameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [cameraState, setCameraState] = useState<CameraState>('checking');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isCapturing, setIsCapturing] = useState(false);

  useEffect(() => {
    checkAndRequestCamera();
    return () => stopCamera();
  }, []);

  useEffect(() => {
    if (cameraState === 'active') {
      stopCamera();
      startCamera();
    }
  }, [facingMode]);

  const checkAndRequestCamera = async () => {
    setCameraState('checking');

    // First, check if camera permission is already denied in browser settings
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });

        // Listen for permission changes (when user changes it in settings)
        permissionStatus.addEventListener('change', () => {
          if (permissionStatus.state === 'granted') {
            startCamera();
          } else if (permissionStatus.state === 'denied') {
            setCameraState('blocked');
            setErrorMessage('Camera is blocked in browser settings');
          }
        });

        if (permissionStatus.state === 'denied') {
          // Permission was previously denied and is now blocked
          // User needs to manually enable it in settings
          setCameraState('blocked');
          setErrorMessage('Camera access is blocked. Please enable it in your browser or device settings.');
          return;
        }

        if (permissionStatus.state === 'granted') {
          // Already have permission, just start the camera
          await startCamera();
          return;
        }

        // Permission state is 'prompt' - show the native dialog
        await requestCameraPermission();

      } catch (err) {
        // Permissions API not supported, try requesting directly
        await requestCameraPermission();
      }
    } else {
      // Permissions API not available, try requesting directly
      await requestCameraPermission();
    }
  };

  const requestCameraPermission = async () => {
    setCameraState('requesting');
    setErrorMessage('');

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      setStream(mediaStream);
      setCameraState('active');

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.setAttribute('playsinline', 'true');
        await videoRef.current.play().catch(() => {});
      }
    } catch (err: any) {
      handleCameraError(err);
    }
  };

  const startCamera = async () => {
    try {
      setErrorMessage('');
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });

      setStream(mediaStream);
      setCameraState('active');

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.setAttribute('playsinline', 'true');
        await videoRef.current.play().catch(() => {});
      }
    } catch (err: any) {
      handleCameraError(err);
    }
  };

  const handleCameraError = (err: any) => {
    console.error('Camera error:', err);

    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      // Check if this is a fresh denial or a blocked permission
      checkIfBlocked();
    } else if (err.name === 'NotFoundError') {
      setCameraState('error');
      setErrorMessage('No camera found on this device');
    } else if (err.name === 'NotReadableError') {
      setCameraState('error');
      setErrorMessage('Camera is being used by another app');
    } else {
      setCameraState('error');
      setErrorMessage('Unable to access camera');
    }
  };

  const checkIfBlocked = async () => {
    // Check if permission is blocked (previously denied)
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
        
        if (permissionStatus.state === 'denied') {
          // Permission is blocked in settings - user needs to manually enable
          setCameraState('blocked');
          setErrorMessage('Camera access is blocked. Please enable it in your browser or device settings.');
        } else {
          // User just denied the prompt
          setCameraState('denied');
          setErrorMessage('Camera access was denied');
        }
      } catch {
        setCameraState('denied');
        setErrorMessage('Camera access was denied');
      }
    } else {
      setCameraState('denied');
      setErrorMessage('Camera access was denied');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleCapture = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      stopCamera();
      onCapture(imageData);
    }

    setIsCapturing(false);
  };

  const toggleFlash = async () => {
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities?.() as any;

    if (capabilities?.torch) {
      try {
        await track.applyConstraints({
          advanced: [{ torch: !isFlashOn } as any]
        });
        setIsFlashOn(!isFlashOn);
      } catch (err) {
        console.error('Flash not supported:', err);
      }
    }
  };

  const flipCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const imageData = reader.result as string;
      stopCamera();
      onCapture(imageData);
    };
    reader.readAsDataURL(file);
  };

  const getDeviceSettingsInfo = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return {
        icon: Settings,
        title: 'Camera Blocked',
        message: 'Camera access is blocked. To enable:',
        steps: ['Open Settings', 'Find Safari or this app', 'Enable Camera access'],
        buttonText: 'Open Settings'
      };
    }
    
    if (/android/.test(userAgent)) {
      return {
        icon: Settings,
        title: 'Camera Blocked',
        message: 'Camera access is blocked. To enable:',
        steps: ['Tap the lock icon in the address bar', 'Or go to Settings > Apps > Permissions'],
        buttonText: 'Try Again'
      };
    }
    
    return {
      icon: Settings,
      title: 'Camera Blocked',
      message: 'Camera access is blocked in your browser settings.',
      steps: ['Click the camera icon in the address bar', 'Allow camera access for this site'],
      buttonText: 'Try Again'
    };
  };

  const renderOverlay = () => {
    // Loading state - checking permissions
    if (cameraState === 'checking' || cameraState === 'requesting') {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="flex flex-col items-center gap-4 text-white">
            <div className="w-12 h-12 border-4 border-white/30 border-t-cyan-500 rounded-full animate-spin" />
            <p className="text-lg font-medium">
              {cameraState === 'checking' ? 'Checking camera access...' : 'Starting camera...'}
            </p>
          </div>
        </div>
      );
    }

    // User just denied the permission prompt - can try again
    if (cameraState === 'denied') {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black p-6">
          <div className="flex flex-col items-center gap-5 max-w-xs text-center">
            <div className="w-20 h-20 rounded-full bg-orange-500/20 flex items-center justify-center">
              <Camera size={40} className="text-orange-400" />
            </div>

            <div>
              <h2 className="text-xl font-bold text-white mb-2">Camera Access Needed</h2>
              <p className="text-white/70 text-sm">
                We need camera access to scan your food. Please allow access when prompted.
              </p>
            </div>

            <button
              onClick={requestCameraPermission}
              className="w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <Camera size={20} />
              Allow Camera Access
            </button>

            <label className="w-full px-6 py-4 bg-white/10 rounded-2xl font-semibold text-white cursor-pointer text-center active:scale-[0.98]">
              Upload Image Instead
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>
        </div>
      );
    }

    // Permission is blocked in settings - needs manual intervention
    if (cameraState === 'blocked') {
      const settingsInfo = getDeviceSettingsInfo();

      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black p-6">
          <div className="flex flex-col items-center gap-5 max-w-xs text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
              <settingsInfo.icon size={40} className="text-red-400" />
            </div>

            <div>
              <h2 className="text-xl font-bold text-white mb-2">{settingsInfo.title}</h2>
              <p className="text-white/70 text-sm mb-3">{settingsInfo.message}</p>
              
              <ul className="text-white/60 text-sm text-left space-y-1">
                {settingsInfo.steps.map((step, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-cyan-400 font-bold">{index + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={checkAndRequestCamera}
              className="w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <RefreshCw size={20} />
              {settingsInfo.buttonText}
            </button>

            <label className="w-full px-6 py-4 bg-white/10 rounded-2xl font-semibold text-white cursor-pointer text-center active:scale-[0.98]">
              Upload Image Instead
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>
        </div>
      );
    }

    // Hardware or other errors
    if (cameraState === 'error') {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black p-6">
          <div className="flex flex-col items-center gap-5 max-w-xs text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center">
              <Camera size={40} className="text-red-400" />
            </div>

            <div>
              <h2 className="text-xl font-bold text-white mb-2">Camera Error</h2>
              <p className="text-white/70 text-sm">{errorMessage}</p>
            </div>

            <button
              onClick={checkAndRequestCamera}
              className="w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <RefreshCw size={20} />
              Try Again
            </button>

            <label className="w-full px-6 py-4 bg-white/10 rounded-2xl font-semibold text-white cursor-pointer text-center active:scale-[0.98]">
              Upload Image Instead
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/60 to-transparent">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white active:scale-95"
          >
            <X size={24} strokeWidth={2.5} />
          </button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-white font-bold text-lg">AgeQ</h1>
          </div>

          <button className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white active:scale-95">
            <HelpCircle size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Camera View */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {cameraState === 'active' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-72 h-72 relative">
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-400 rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-cyan-400 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-cyan-400 rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyan-400 rounded-br-lg" />
            </div>
          </div>
        )}

        {renderOverlay()}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Bottom Controls */}
      {cameraState === 'active' && (
        <div className="bg-black/80 backdrop-blur-sm p-4">
          <div className="flex items-center justify-around mb-4">
            <button
              className="flex flex-col items-center gap-1.5 text-white"
              onClick={handleCapture}
              disabled={isCapturing}
            >
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <ScanBarcode size={22} strokeWidth={2} />
              </div>
              <span className="text-[10px] font-semibold">Scan Food</span>
            </button>

            <button className="flex flex-col items-center gap-1.5 text-white/70">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <ScanBarcode size={22} strokeWidth={2} />
              </div>
              <span className="text-[10px] font-semibold">Barcode</span>
            </button>

            <label className="flex flex-col items-center gap-1.5 text-white/70 cursor-pointer">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <FileText size={22} strokeWidth={2} />
              </div>
              <span className="text-[10px] font-semibold">Food Label</span>
            </label>

            <label className="flex flex-col items-center gap-1.5 text-white/70 cursor-pointer">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <ImageIcon size={22} strokeWidth={2} />
              </div>
              <span className="text-[10px] font-semibold">Library</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
          </div>

          <div className="flex items-center justify-center gap-6">
            <button
              onClick={toggleFlash}
              className={`w-11 h-11 rounded-full ${isFlashOn ? 'bg-yellow-500' : 'bg-white/20'} flex items-center justify-center active:scale-95`}
            >
              <Zap size={18} className={isFlashOn ? 'text-white' : 'text-white/70'} strokeWidth={2.5} />
            </button>

            <button
              onClick={handleCapture}
              disabled={isCapturing}
              className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-lg active:scale-95 disabled:opacity-50"
            >
              <div className={`w-14 h-14 rounded-full border-4 border-gray-900 ${isCapturing ? 'bg-gray-300' : ''}`} />
            </button>

            <button
              onClick={flipCamera}
              className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center active:scale-95"
            >
              <RotateCcw size={18} className="text-white/70" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}